import { useEffect, useState } from "react";

import { LogOut, Menu } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import NotificationMenu from "./notifications";
import SidebarLogo from "../../assets/icons/ep-icon-blue.png";
import { Button } from "../common/customDesignedComponents/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../common/customDesignedComponents/dropdown-menu";
import { clearLocalStoarge, isIosApp, redirectToLogin } from "../helpers/utils";
import { serviceProviderSignInAction } from "../../redux/slices/serviceProvider/SPSignINSlice";
import { serviceProviderProfileAction } from "../../redux/slices/serviceProvider/servicerProfile/servicerProfileSlice";

interface ServicerDashboardHeaderProps {
  onMenuClick?: () => void;
  isSidebarOpen?: boolean;
}

export const ServicerDashboardHeader = ({
  onMenuClick,
  isSidebarOpen = true,
}: ServicerDashboardHeaderProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isBelow510, setIsBelow510] = useState(false);
  const [isBelow699, setIsBelow699] = useState(false);

  const { servicerProfileDetails } = useSelector(
    (state: any) => state?.servicerProfile,
  );

  const profile = servicerProfileDetails?.data;
  const displayName = [profile?.first_name, profile?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq510 = window.matchMedia("(max-width: 510px)");
    const mq699 = window.matchMedia("(max-width: 698px)");
    const update510 = () => setIsBelow510(!!mq510.matches);
    const update699 = () => setIsBelow699(!!mq699.matches);
    update510();
    update699();
    mq510.addEventListener?.("change", update510);
    mq699.addEventListener?.("change", update699);
    return () => {
      mq510.removeEventListener?.("change", update510);
      mq699.removeEventListener?.("change", update699);
    };
  }, []);

  const handleSignOut = () => {
    dispatch(
      serviceProviderSignInAction.userSignoutData({
        url: "service-provider/logout",
        data: {},
        token: true,
      }),
    );
    dispatch(serviceProviderProfileAction.logoutPasswordRequired());
    dispatch(serviceProviderSignInAction.clearSigninData());
    const iosFlag = isIosApp();
    clearLocalStoarge();
    localStorage.setItem("ivokeNativeMethod", String(iosFlag));
    redirectToLogin(navigate);
  };

  return (
    <header
      className={`h-16 bg-card border-b border-border flex items-center justify-between fixed top-0 right-0 z-40 transition-all duration-200 ${
        isBelow699 && !isSidebarOpen ? "left-0" : isSidebarOpen ? "left-64" : "left-16"
      } ${isSidebarOpen && isBelow510 ? "px-1" : "px-6"}`}
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onMenuClick}
          className="py-2 px-1 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Toggle menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        {isBelow699 && !isSidebarOpen ? (
          <img src={SidebarLogo} alt="EquityProtect" className="h-7 w-auto" />
        ) : null}
      </div>

      <div
        className={`flex items-center ${isSidebarOpen && isBelow510 ? "justify-end px-1 gap-2" : "justify-between gap-4"}`}
      >
        <div className="tailwind_component">
          <NotificationMenu isServiceProvider />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="hidden sm:inline-flex gap-2 max-w-[180px]"
            >
              <span className="truncate">{displayName || "Account"}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="tailwind_component w-48">
            <DropdownMenuItem onClick={() => navigate("/servicer-profile")}>
              My profile
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleSignOut}
              className="text-destructive"
            >
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Mobile-only sign out; desktop uses the account menu above. */}
        <Button
          type="button"
          variant="destructive"
          size="sm"
          onClick={handleSignOut}
          className={`rounded-md sm:hidden ${
            isSidebarOpen && isBelow510 ? "h-8 w-8 p-0" : "px-3 h-8 text-xs"
          }`}
          aria-label="Sign out"
          title="Sign out"
        >
          {isSidebarOpen && isBelow510 ? (
            <LogOut className="h-4 w-4" />
          ) : (
            "Sign out"
          )}
        </Button>
      </div>
    </header>
  );
};
